import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import { useMutation } from '@tanstack/react-query';
import { getModule } from '@jwp/ott-common/src/modules/container';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import AccountController from '@jwp/ott-common/src/controllers/AccountController';
import { isTruthy } from '@jwp/ott-common/src/utils/common';
import { logError } from '@jwp/ott-common/src/logger';
import { formatConsents, formatConsentValues } from '@jwp/ott-common/src/utils/collection';
import useToggle from '@jwp/ott-hooks-react/src/useToggle';
import Visibility from '@jwp/ott-theme/assets/icons/visibility.svg?react';
import VisibilityOff from '@jwp/ott-theme/assets/icons/visibility_off.svg?react';
import env from '@jwp/ott-common/src/env';

import type { FormSectionContentArgs, FormSectionProps } from '../../../../components/Form/FormSection';
import Alert from '../../../../components/Alert/Alert';
import Button from '../../../../components/Button/Button';
import Form from '../../../../components/Form/Form';
import IconButton from '../../../../components/IconButton/IconButton';
import FormFeedback from '../../../../components/FormFeedback/FormFeedback';
import TextField from '../../../../components/form-fields/TextField/TextField';
import Icon from '../../../../components/Icon/Icon';
import { modalURLFromLocation } from '../../../../utils/location';
import { useAriaAnnouncer } from '../../../../containers/AnnouncementProvider/AnnoucementProvider';
import userStyles from '../../User.module.scss';

import styles from './AccountSection.module.scss';

interface FormErrors {
  email?: string;
  confirmationPassword?: string;
  firstName?: string;
  lastName?: string;
  form?: string;
}

const AccountSection = (): JSX.Element => {
  const accountController = getModule(AccountController);

  const { t } = useTranslation('user');
  const announce = useAriaAnnouncer();
  const navigate = useNavigate();
  const location = useLocation();
  const [viewPassword, toggleViewPassword] = useToggle();
  const exportData = useMutation({ mutationFn: accountController.exportAccountData });
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const exportDataMessage = exportData.isSuccess ? t('account.export_data_success') : t('account.export_data_error');
  const { canUpdateEmail } = accountController.getFeatures();
  const isExternalAccountDeletion = !!env.APP_DELETE_ACCOUNT_URL;

  useEffect(() => {
    if (exportData.isSuccess || exportData.isError) {
      setIsAlertVisible(true);
    }
  }, [exportData.isSuccess, exportData.isError]);

  const { customer, customerConsents, publisherConsents } = useAccountStore(
    ({ user, customerConsents, publisherConsents }) => ({
      customer: user,
      customerConsents,
      publisherConsents,
    }),
    shallow,
  );

  const { canExportAccountData, canDeleteAccount } = accountController.getFeatures();
  // users authenticated with social (register_source: facebook, google, twitter) do not have password by default
  const registerSource = customer?.metadata?.register_source;
  const isSocialLogin = (registerSource && registerSource !== 'inplayer') || false;
  const shouldAddPassword = (isSocialLogin && !customer?.metadata?.has_password) || false;

  // load consents (move to `useConsents` hook?)
  useEffect(() => {
    if (!publisherConsents) {
      accountController.getPublisherConsents();

      return;
    }
  }, [accountController, publisherConsents]);

  const consents = useMemo(() => formatConsents(publisherConsents, customerConsents), [publisherConsents, customerConsents]);

  const consentsValues = useMemo(() => formatConsentValues(publisherConsents, customerConsents), [publisherConsents, customerConsents]);

  const initialValues = useMemo(
    () => ({
      ...customer,
      consents,
      consentsValues,
      confirmationPassword: '',
    }),
    [customer, consents, consentsValues],
  );

  function translateErrors(errors?: string[]): FormErrors {
    const formErrors: FormErrors = {};
    // Some errors are combined in a single CSV string instead of one string per error
    errors
      ?.flatMap((e) => e.split(','))
      .forEach((error) => {
        switch (error.trim()) {
          case 'Invalid param email':
            formErrors.form = t('account.errors.validation_error');
            formErrors.email = t('account.errors.invalid_param_email');
            break;
          case 'Customer email already exists':
            formErrors.form = t('account.errors.validation_error');
            formErrors.email = t('account.errors.email_exists');
            break;
          case 'Please enter a valid e-mail address.':
            formErrors.form = t('account.errors.validation_error');
            formErrors.email = t('account.errors.please_enter_valid_email');
            break;
          case 'Invalid confirmationPassword': {
            formErrors.form = t('account.errors.validation_error');
            formErrors.confirmationPassword = t('account.errors.invalid_password');
            break;
          }
          case 'firstName can have max 50 characters.': {
            formErrors.form = t('account.errors.validation_error');
            formErrors.firstName = t('account.errors.first_name_too_long');
            break;
          }
          case 'lastName can have max 50 characters.': {
            formErrors.form = t('account.errors.validation_error');
            formErrors.lastName = t('account.errors.last_name_too_long');
            break;
          }
          case 'Email update not supported': {
            formErrors.form = t('account.errors.email_update_not_supported');
            break;
          }
          case 'Wrong email/password combination': {
            formErrors.form = t('account.errors.wrong_combination');
            break;
          }
          default: {
            formErrors.form = t('account.errors.unknown_error');
            logError('Account', 'Unknown error', { error });
            break;
          }
        }
      });

    return formErrors;
  }

  function formSection(props: FormSectionProps<typeof initialValues, FormErrors>) {
    return {
      ...props,
      className: userStyles.panel,
      panelHeaderClassName: userStyles.panelHeader,
      saveButton: t('account.save'),
      cancelButton: t('account.cancel'),
      content: (args: FormSectionContentArgs<typeof initialValues, string[]>) => {
        // This function just allows the sections below to use the FormError type instead of an array of errors
        const formErrors = translateErrors(args.errors);

        // Render the section content, but also add a warning text if there's a form level error
        return (
          <>
            {formErrors?.form ? <FormFeedback variant="error">{formErrors.form}</FormFeedback> : null}
            {props.content?.({ ...args, errors: formErrors })}
          </>
        );
      },
    };
  }

  return (
    <>
      <h1 className="hideUntilFocus">{t('nav.account')}</h1>

      <Form initialValues={initialValues}>
        {[
          formSection({
            label: t('account.email'),
            onSubmit: async (values) => {
              if (!values.email || !values.confirmationPassword) {
                throw new Error('Wrong email/password combination');
              }
              const response = await accountController.updateUser({
                email: values.email || '',
                confirmationPassword: values.confirmationPassword,
              });

              announce(t('account.update_success', { section: t('account.email') }), 'success');

              return response;
            },
            editButton: t('account.edit_account'),
            readOnly: !canUpdateEmail,
            content: (section) => (
              <>
                <TextField
                  name="email"
                  label={t('account.email')}
                  value={section.values.email || ''}
                  onChange={section.onChange}
                  error={!!section.errors?.email}
                  helperText={section.errors?.email}
                  disabled={section.isBusy}
                  editing={section.isEditing}
                  autoComplete="email"
                  required
                />
                {section.isEditing && (
                  <TextField
                    name="confirmationPassword"
                    label={t('account.confirm_password')}
                    value={section.values.confirmationPassword}
                    onChange={section.onChange}
                    error={!!section.errors?.confirmationPassword}
                    helperText={section.errors?.confirmationPassword}
                    type={viewPassword ? 'text' : 'password'}
                    disabled={section.isBusy}
                    autoComplete="current-password"
                    rightControl={
                      <IconButton aria-label={t('account.view_password')} onClick={() => toggleViewPassword()} aria-pressed={viewPassword}>
                        <Icon icon={viewPassword ? Visibility : VisibilityOff} />
                      </IconButton>
                    }
                    required
                  />
                )}
              </>
            ),
          }),
          canExportAccountData &&
            formSection({
              label: t('account.export_data_title'),
              content: (section) => (
                <div className={styles.textWithButtonContainer}>
                  <div>
                    <Trans t={t} i18nKey="account.export_data_body" values={{ email: section.values.email }} />
                  </div>
                  <div>
                    <Button
                      label={t('account.export_data_title')}
                      type="button"
                      disabled={exportData.isPending}
                      onClick={async () => {
                        exportData.mutate();
                      }}
                    />
                  </div>
                </div>
              ),
            }),
          canDeleteAccount &&
            formSection({
              label: t('account.delete_account.title'),
              content: () => (
                <div className={styles.textWithButtonContainer}>
                  <div>{t('account.delete_account.body')}</div>
                  <div>
                    <Button
                      label={t('account.delete_account.title')}
                      type="button"
                      variant="danger"
                      onClick={() => {
                        navigate(modalURLFromLocation(location, shouldAddPassword ? 'warning-account-deletion' : 'delete-account'));
                      }}
                    />
                  </div>
                </div>
              ),
            }),
          isExternalAccountDeletion &&
            formSection({
              label: t('account.delete_account.title'),
              content: () => (
                <div className={styles.textWithButtonContainer}>
                  <div>{t('account.delete_account.body')}</div>
                  <div>
                    <Button
                      label={t('account.delete_account.title')}
                      type="button"
                      variant="danger"
                      onClick={() => {
                        navigate(modalURLFromLocation(location, 'delete-account'));
                      }}
                    />
                  </div>
                </div>
              ),
            }),
        ].filter(isTruthy)}
      </Form>
      {canExportAccountData && (
        <Alert open={isAlertVisible} message={exportDataMessage} onClose={() => setIsAlertVisible(false)} isSuccess={exportData.isSuccess} />
      )}
    </>
  );
};

export default AccountSection;
